import { memo, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from '@aimsos/i18n';
import { CartContainer, EntityList } from '@aimsos/ui';

import { widgetTypeIcon } from '../constants/widgetTypeIcons.js';
import {
  stopEntityListInteractiveKeyDown,
  stopEntityListInteractivePropagation,
  WIDGET_ENTITY_LIST_DROPDOWN_PROPS,
  wrapEntityListMenuAction,
} from '../utils/widgetEntityListHandlers.js';
import { widgetTypeLabel } from '../utils/widgetTypeLabel.js';

import { Eye, LayoutDashboard, Pencil, Trash2 } from 'lucide-react';

/**
 * Two scoped overrides on `EntityList`'s own `className` prop — same
 * technique as governance's `TRUTH_PLANE_ENTITY_LIST_CLASS`: a more specific
 * selector wins over `EntityList`'s hardcoded utility classes (packages/ui
 * `EntityList.jsx` / `entityListClasses.js`) without editing that shared file.
 *
 * 1. Shrinks the eye (`secondaryCta`) button to the 26×24 spec — its className
 *    is hardcoded (`h-[27px] px-3 py-1`); the kebab is unaffected since its own
 *    `!size-7` is `!important` and always wins regardless.
 * 2. Makes the title truncate instead of wrapping the row. `entityListTitleClass`
 *    is `shrink-0` with no `truncate`/`overflow-hidden`, and its parent
 *    (`entityListHeaderLeftClass`) is `flex-wrap` — so a long name currently
 *    wraps the row onto a second line instead of ellipsing. The title is the
 *    2nd child of header-left here because this row always passes `icon`.
 */
const WIDGET_LIBRARY_ROW_ACTION_CLASS = [
  '[&>div>div:first-child>div:last-child_button]:h-6',
  '[&>div>div:first-child>div:last-child_button]:min-h-6',
  '[&>div>div:first-child>div:last-child_button]:w-[26px]',
  '[&>div>div:first-child>div:last-child_button]:min-w-[26px]',
  '[&>div>div:first-child>div:last-child_button]:px-0',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:min-w-0',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:shrink',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:overflow-hidden',
  '[&>div>div:first-child>div:first-child>*:nth-child(2)]:truncate',
].join(' ');

/**
 * Status → DS badge variant. The two the backend models: a widget is created
 * as a draft and becomes published. No colour literals, no per-row branching
 * in JSX.
 */
const STATUS_VARIANT = { published: 'success', draft: 'neutral' };

/**
 * One catalog row (CT-5187).
 *
 * Per **DS-1** this is a Governance-density row — `CartContainer` + `EntityList`,
 * the same composition as `truth-plane`'s `TruthPlaneEntityCard` — **not** the
 * prototype's card with a visual mini-preview. The preview lives in the detail
 * panel and in the builder's live preview instead. Thomas signed off on this
 * divergence (CT-5189, 2026-09-09): "el listado de los widgets permanece
 * igual ... con el List Card que usamos."
 *
 * `memo()` matters here: the catalog renders up to 48 of these, so the page
 * passes one stable callback per intent and the row supplies the id itself.
 */
const WidgetLibraryRow = memo(function WidgetLibraryRow({
  widget,
  selected,
  onPreview,
  onEdit,
  onDelete,
  onAddToDashboard,
}) {
  const { t } = useTranslation('dashboards');

  const { icon, highlight } = widgetTypeIcon(widget.type);

  // The row owns the id so the page can pass one stable handler per intent
  // instead of an inline arrow per row (which would defeat memo()).
  const handlePreview = useCallback(() => onPreview?.(widget), [onPreview, widget]);
  const handleEdit = useCallback(() => onEdit?.(widget.id), [onEdit, widget.id]);
  const handleDelete = useCallback(() => onDelete?.(widget), [onDelete, widget]);
  const handleAddToDashboard = useCallback(
    () => onAddToDashboard?.(widget),
    [onAddToDashboard, widget],
  );

  const typeLabel = widgetTypeLabel(widget.type, t);

  const primaryMeta = useMemo(
    () => (widget.source ? [{ label: widget.source }] : []),
    [widget.source],
  );

  // The placement count is only shown when the backend actually reports one.
  // `null` means "the WidgetDefinition contract does not expose placements",
  // and printing `Used on 0 dashboards` for that would assert something false.
  const secondaryMeta = useMemo(
    () => [
      { label: typeLabel },
      ...(widget.usedOnDashboards == null
        ? []
        : [{ label: t('widgetLibrary.row.usedOnDashboards', { count: widget.usedOnDashboards }) }]),
    ],
    [t, typeLabel, widget.usedOnDashboards],
  );

  const tags = useMemo(() => {
    const list = [
      {
        label: t(`widgetLibrary.row.status.${widget.status}`),
        variant: STATUS_VARIANT[widget.status] ?? 'neutral',
      },
    ];
    // Governance is a property of the widget, not a decoration: only governed
    // widgets get the tag.
    if (widget.governed) {
      list.push({ label: t('widgetLibrary.row.governed'), variant: 'informative' });
    }
    return list;
  }, [t, widget.governed, widget.status]);

  // Same composition as truth-plane's `TruthPlaneEntityCard`: an icon-only
  // `secondaryCta` (no text label). `EntityList` renders the divider between
  // this and the kebab automatically whenever a CTA is present — nothing to
  // add by hand. `EntityList` doesn't forward an `aria-label` for
  // `secondaryCta` the way it does for the kebab's `triggerAriaLabel`, so the
  // accessible name has to ride along inside `label` itself as sr-only text.
  const secondaryCta = useMemo(
    () => ({
      label: (
        <>
          <Eye size={16} aria-hidden />
          <span className="sr-only">{t('widgetLibrary.row.preview', { name: widget.name })}</span>
        </>
      ),
      variant: 'secondary',
      onClick: handlePreview,
    }),
    [handlePreview, t, widget.name],
  );

  const dropdown = useMemo(
    () => ({
      props: {
        ...WIDGET_ENTITY_LIST_DROPDOWN_PROPS,
        triggerAriaLabel: t('widgetLibrary.row.actionsFor', { name: widget.name }),
      },
      items: [
        {
          label: t('widgetLibrary.row.addToDashboard'),
          icon: LayoutDashboard,
          onClick: wrapEntityListMenuAction(handleAddToDashboard),
        },
        {
          label: t('widgetLibrary.row.edit'),
          icon: Pencil,
          onClick: wrapEntityListMenuAction(handleEdit),
        },
        {
          label: t('widgetLibrary.row.delete'),
          icon: Trash2,
          // `EntityList` forwards only `variant` to `Dropdown.Item`; a
          // `destructive` flag is dropped and Delete renders like any other row.
          variant: 'danger',
          onClick: wrapEntityListMenuAction(handleDelete),
        },
      ],
    }),
    [handleAddToDashboard, handleDelete, handleEdit, t, widget.name],
  );

  return (
    <CartContainer size="S" variant="neutral" selected={selected} onClick={handlePreview}>
      <EntityList
        // Without these, activating the kebab or the eye CTA bubbles up to the
        // CartContainer and opens the detail panel too — by mouse *and* by
        // keyboard, where CartContainer also swallows the Enter/Space.
        onClick={stopEntityListInteractivePropagation}
        onKeyDown={stopEntityListInteractiveKeyDown}
        className={WIDGET_LIBRARY_ROW_ACTION_CLASS}
        title={widget.name}
        icon={icon}
        iconHighlight={{ bgColor: highlight, iconColor: highlight }}
        primaryMeta={primaryMeta}
        secondaryMeta={secondaryMeta}
        tags={tags}
        secondaryCta={secondaryCta}
        dropdown={dropdown}
      />
    </CartContainer>
  );
});

WidgetLibraryRow.propTypes = {
  widget: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    source: PropTypes.string,
    type: PropTypes.string,
    status: PropTypes.string,
    governed: PropTypes.bool,
    usedOnDashboards: PropTypes.number,
  }).isRequired,
  selected: PropTypes.bool,
  onPreview: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onAddToDashboard: PropTypes.func,
};

export default WidgetLibraryRow;
